import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { palette, Icon } from '@m12s/component-library';
import BaseModal from 'components/BaseModal';
import { ModalButton } from 'components/BaseModal/styled';
import { minus, plus } from 'lib/icons';
import { actionClosePartCountMultiplierDialog } from 'lib/actions';
import { PCM_MAX_VALUE } from 'lib/constants/partCountMultiplier';
import {
  getPartCountMultiplierDialogOpen,
  getPartCountMultiplierDialogDefaultValue,
} from 'lib/selectors/getPartCountMultiplierDialog';
import PartCountMultiplierDialogEventEmitter from './emitter';

const TwoInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const EqualsSign = styled.span`
  font-size: 1.5rem;
  color: ${palette.Grey700};
  flex-shrink: 0;
  line-height: 1;
`;

const UnitInput = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid ${palette.Grey400};
  border-radius: 4px;
  background: ${palette.White};
  overflow: hidden;
  height: 2.5rem;
  flex: 1 0 0;
  min-width: 0;
`;

const UnitInputField = styled.input`
  flex: 1 0 0;
  min-width: 0;
  height: 100%;
  padding: 0 0.625rem;
  border: none;
  border-right: 1px solid ${palette.Grey300};
  background: ${palette.White};
  font-size: 0.875rem;
  font-weight: 400;
  color: ${palette.Grey700};
  letter-spacing: 0.07px;
  outline: none;
`;

const StepButton = styled.button`
  width: 2.5rem;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${palette.White};
  border: none;
  border-left: 1px solid ${palette.Grey300};
  color: ${palette.Grey700};
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    color: ${palette.Grey400};
    cursor: default;
  }
`;

const MAX_VALUE = PCM_MAX_VALUE;

// Clamp the parts side to [1, MAX] WITHOUT rounding — parts may be fractional
// (a PCM of 3.5 is "3.5 parts = 1 cycle"); a sub-1 multiplier is expressed on
// the whole-number cycles side instead.
const clampParts = (raw) => {
  return Math.min(Math.max(raw, 1), MAX_VALUE);
};

// Round to 2dp so whole-number steps on a fractional value stay clean (3.5 + 1
// → 4.5, not 4.499…) and typed input doesn't carry float drift.
const round2 = (n) => {
  return Math.round(n * 100) / 100;
};

export const derivePartsCyclesPair = (floatValue) => {
  if (!floatValue || floatValue <= 0) return { cycles: 1, parts: 1 };
  if (floatValue >= 1) {
    return { cycles: 1, parts: clampParts(floatValue) };
  }
  return { cycles: Math.min(Math.round(1 / floatValue), MAX_VALUE), parts: 1 };
};

// Render a PCM number without a trailing ".0" (3.5 → "3.5", 4 → "4").
const formatNumber = (n) => {
  return Number.isInteger(n) ? String(n) : String(Number(round2(n)));
};

// The card header multiplier label, e.g. "3.5×". Shares formatNumber with the
// ratio subtitle so header and subtitle can't diverge, float drift (3.4999) is
// rounded, and whole values show as "4×" not "4.0×".
export const formatMultiplierLabel = (value) => {
  const safe = value && Number.isFinite(value) && value > 0 ? value : 1;
  return `${formatNumber(safe)}×`;
};

// "3.5 parts = 1 cycle" — the paired phrasing the editor uses, so the card and
// the dialog never describe the same multiplier differently. Parts may be
// fractional; cycles are whole.
export const formatPartsCyclesRatio = (value) => {
  const { parts, cycles } = derivePartsCyclesPair(value);
  const partLabel = `${formatNumber(parts)} ${parts === 1 ? 'part' : 'parts'}`;
  const cycleLabel = `${formatNumber(cycles)} ${
    cycles === 1 ? 'cycle' : 'cycles'
  }`;
  return `${partLabel} = ${cycleLabel}`;
};

const UnitField = ({ value, onChange, unit, max, allowDecimal }) => {
  const [focused, setFocused] = useState(false);
  const [rawInput, setRawInput] = useState('');
  const inputRef = useRef(null);

  // Select all text after the re-render that switches displayValue to the raw number.
  // useEffect runs post-render, so the DOM reflects the new value before we select.
  useEffect(() => {
    if (focused && inputRef.current) {
      inputRef.current.select();
    }
  }, [focused]);

  const displayValue = focused
    ? rawInput
    : `${value} ${unit}${value === 1 ? '' : 's'}`;

  return (
    <UnitInput>
      {/* type="text" so the unit label ("1 cycle") can be shown inside the field; inputMode triggers numeric keyboard on mobile */}
      <UnitInputField
        ref={inputRef}
        type="text"
        inputMode={allowDecimal ? 'decimal' : 'numeric'}
        value={displayValue}
        onFocus={() => {
          setRawInput(String(value));
          setFocused(true);
        }}
        onBlur={(e) => {
          setFocused(false);
          const parsed = allowDecimal
            ? parseFloat(e.target.value)
            : parseInt(e.target.value, 10);
          if (!Number.isNaN(parsed) && parsed >= 1) {
            const next = allowDecimal ? round2(parsed) : parsed;
            onChange(Math.min(max, next));
          }
        }}
        onChange={(e) => {
          setRawInput(e.target.value);
        }}
      />
      <StepButton
        type="button"
        disabled={value <= 1}
        onClick={() => {
          onChange(round2(Math.max(1, value - 1)));
        }}
      >
        <Icon icon={minus} size="1rem" />
      </StepButton>
      <StepButton
        type="button"
        disabled={value >= max}
        onClick={() => {
          onChange(round2(Math.min(max, value + 1)));
        }}
      >
        <Icon icon={plus} size="1rem" />
      </StepButton>
    </UnitInput>
  );
};

UnitField.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  unit: PropTypes.string.isRequired,
  max: PropTypes.number.isRequired,
  // Parts allow fractional entry (3.5); cycles stay whole. Steppers move by 1
  // on both, so a 3.5 steps to 4.5 / 2.5.
  allowDecimal: PropTypes.bool,
};

UnitField.defaultProps = {
  allowDecimal: false,
};

export const PartCountMultiplierDialogBase = ({
  defaultValue,
  onSave,
  onCancel,
}) => {
  const initial = derivePartsCyclesPair(defaultValue);
  const [cycles, setCycles] = useState(initial.cycles);
  const [parts, setParts] = useState(initial.parts);

  const handleSave = useCallback(() => {
    onSave(round2(parts / cycles));
  }, [onSave, parts, cycles]);

  return (
    <BaseModal
      isOpen
      onClose={onCancel}
      header="Edit part count method"
      details={
        <TwoInputRow>
          <UnitField
            value={parts}
            onChange={setParts}
            unit="part"
            max={MAX_VALUE}
            allowDecimal
          />
          <EqualsSign>=</EqualsSign>
          <UnitField
            value={cycles}
            onChange={setCycles}
            unit="cycle"
            max={MAX_VALUE}
          />
        </TwoInputRow>
      }
      buttons={[
        <ModalButton
          key="cancel"
          onClick={onCancel}
          customStyles={`
            background: ${palette.White};
            border: 1px solid ${palette.Grey400};
            color: ${palette.Grey700};
            text-shadow: none;
          `}
          hoverStyles={`background: ${palette.Grey100};`}
        >
          Cancel
        </ModalButton>,
        <ModalButton
          key="save"
          onClick={handleSave}
          customStyles={`
            background: ${palette.Indigo600};
            border: none;
            color: ${palette.White};
            text-shadow: none;
          `}
          hoverStyles={`background: ${palette.Indigo700};`}
        >
          Save
        </ModalButton>,
      ]}
    />
  );
};

PartCountMultiplierDialogBase.propTypes = {
  defaultValue: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

PartCountMultiplierDialogBase.defaultProps = {
  defaultValue: 1,
};

const PartCountMultiplierDialog = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(getPartCountMultiplierDialogOpen);
  const defaultValue = useSelector(getPartCountMultiplierDialogDefaultValue);

  const handleSave = useCallback(
    (value) => {
      PartCountMultiplierDialogEventEmitter.fireAndForget('success', value);
      dispatch(actionClosePartCountMultiplierDialog());
    },
    [dispatch]
  );

  const handleCancel = useCallback(() => {
    PartCountMultiplierDialogEventEmitter.fireAndForget('cancel');
    dispatch(actionClosePartCountMultiplierDialog());
  }, [dispatch]);

  if (!isOpen) return null;

  return (
    <PartCountMultiplierDialogBase
      defaultValue={defaultValue}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
};

export default PartCountMultiplierDialog;
